import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useT } from "../lib/i18n";
import { openBooking } from "../lib/booking";

const LANGS = [
    { code: "en", label: "EN" },
    { code: "it", label: "IT" },
    { code: "de", label: "DE" },
    { code: "es", label: "ES" },
];

export default function Footer() {
    const { t, lang, setLang } = useT();
    const navigate = useNavigate();
    const year = new Date().getFullYear();

    return (
        <footer className="bg-[#1C1C1C] text-[#F9F9F7] mt-32" data-testid="site-footer">
            <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-24 md:pt-32 pb-12">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8">
                    <div className="md:col-span-7">
                        <p className="eyebrow text-[#9B7A58]">Casati Consulting · Zürich</p>
                        <h2
                            className="font-display mt-6 text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95] font-medium"
                            style={{ letterSpacing: "-0.03em" }}
                        >
                            {t.footer.tagA}
                            <br />
                            {t.footer.tagB}
                        </h2>
                        <p className="mt-8 max-w-md text-[#F9F9F7]/70 leading-relaxed">
                            {t.footer.tagline}
                        </p>
                        <button
                            type="button"
                            onClick={() => openBooking(navigate)}
                            className="mt-10 inline-flex items-center gap-2 group"
                            data-testid="footer-cta-book"
                        >
                            <span className="link-underline text-[13px] uppercase tracking-[0.22em] font-bold">
                                {t.footer.book}
                            </span>
                            <ArrowUpRight
                                size={18}
                                strokeWidth={1.5}
                                className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                            />
                        </button>
                    </div>

                    <div className="md:col-span-2">
                        <p className="eyebrow">{t.common.site}</p>
                        <ul className="mt-6 space-y-3 text-sm text-[#F9F9F7]/80">
                            <li><Link to="/" className="hover:text-[#9B7A58]" data-testid="footer-link-home">{t.nav.home}</Link></li>
                            <li><Link to="/about" className="hover:text-[#9B7A58]" data-testid="footer-link-about">{t.nav.about}</Link></li>
                            <li><Link to="/services" className="hover:text-[#9B7A58]" data-testid="footer-link-services">{t.nav.services}</Link></li>
                            <li><Link to="/insights" className="hover:text-[#9B7A58]" data-testid="footer-link-insights">{t.nav.insights}</Link></li>
                            <li><Link to="/contact" className="hover:text-[#9B7A58]" data-testid="footer-link-contact">{t.nav.contact}</Link></li>
                            <li><Link to="/careers" className="hover:text-[#9B7A58]" data-testid="footer-link-careers">{t.nav.careers}</Link></li>
                        </ul>
                    </div>

                    <div className="md:col-span-3">
                        <p className="eyebrow">{t.common.office}</p>
                        <address className="not-italic mt-6 text-sm text-[#F9F9F7]/80 leading-relaxed">
                            {t.common.byAppointment}
                            <br />
                            Swiss Life Select
                            <br />
                            Europastrasse 18
                            <br />
                            8152 Glattbrugg
                            <br />
                            <a
                                href="tel:+41775013627"
                                className="hover:text-[#9B7A58] link-underline"
                                data-testid="footer-phone"
                            >
                                +41 77 501 36 27
                            </a>
                            <br />
                            <a
                                href="mailto:matteo.casati@swisslife-select.ch"
                                className="hover:text-[#9B7A58] link-underline"
                                data-testid="footer-email"
                            >
                                matteo.casati@swisslife-select.ch
                            </a>
                        </address>
                        <p className="eyebrow mt-10">{t.common.languages}</p>
                        <div
                            className="mt-4 flex items-center text-[11px] uppercase tracking-[0.2em] font-bold"
                            role="group"
                            aria-label="Language switcher"
                            data-testid="footer-lang-switcher"
                        >
                            {LANGS.map((l, i) => (
                                <React.Fragment key={l.code}>
                                    {i > 0 && <span className="text-[#F9F9F7]/25 px-1">·</span>}
                                    <button
                                        type="button"
                                        onClick={() => setLang(l.code)}
                                        className={`px-1.5 py-1 transition-colors ${
                                            lang === l.code
                                                ? "text-[#9B7A58]"
                                                : "text-[#F9F9F7]/55 hover:text-[#F9F9F7]"
                                        }`}
                                        data-testid={`footer-lang-${l.code}`}
                                        aria-pressed={lang === l.code}
                                    >
                                        {l.label}
                                    </button>
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="mt-20 pt-8 border-t border-[#F9F9F7]/10 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-xs text-[#F9F9F7]/50">
                    <p>{t.common.disclaimer.replace("{year}", year)}</p>
                    <p className="tracking-[0.18em] uppercase">{t.common.footerRight}</p>
                </div>
            </div>
        </footer>
    );
}
